/**
 * What still waits on a decision, per document, split the way the sidebar
 * splits it: findings and suggestions.
 *
 * Findings are every compliance gap the Findings page lists for a document —
 * regulatory findings plus the gap suggestions filed under it (control
 * amendments). Suggestions are the housekeeping rest (rename, owner, review
 * cadence, mappings…). The shelf used to add both into one red "Findings"
 * number, so a document with nothing but a suggested rename read as having a
 * finding. The two are counted apart here, so the red count is findings only
 * and the suggestion count can stay neutral.
 *
 * Shared by the policy and procedure shelves and the overview's document
 * table, so the three can never disagree.
 *
 * @module components/regulation/policy/openWorkByPolicy
 */
import { isGapSuggestionKind } from "@repo/shared/findingPlanning";
import { isOpenFindingStatus, isOpenSuggestionStatus } from "@repo/shared/reviewCounts";

export interface OpenWork {
  /** Open findings plus open gap suggestions on the document: the red count. */
  readonly findings: number;
  /** Open suggestions that are not gaps: the neutral count. */
  readonly suggestions: number;
}

export const NO_OPEN_WORK: OpenWork = { findings: 0, suggestions: 0 };

export function openWorkByPolicy(
  findings: ReadonlyArray<{ readonly policyId: string; readonly status: string }>,
  suggestions: ReadonlyArray<{ readonly policyId: string | null; readonly status: string; readonly kind: string }>,
): ReadonlyMap<string, OpenWork> {
  const counts = new Map<string, { findings: number; suggestions: number }>();
  const entry = (policyId: string) => {
    let found = counts.get(policyId);
    if (found === undefined) {
      found = { findings: 0, suggestions: 0 };
      counts.set(policyId, found);
    }
    return found;
  };
  for (const finding of findings) {
    if (isOpenFindingStatus(finding.status)) entry(finding.policyId).findings += 1;
  }
  for (const suggestion of suggestions) {
    // A suggestion with no document (a missing document, the company
    // profile) belongs to the library, not a row.
    if (suggestion.policyId === null || !isOpenSuggestionStatus(suggestion.status)) continue;
    if (isGapSuggestionKind(suggestion.kind)) entry(suggestion.policyId).findings += 1;
    else entry(suggestion.policyId).suggestions += 1;
  }
  return counts;
}

/** Open findings per document, the input `policyFreshness` reads. */
export const openFindingsByPolicy = (work: ReadonlyMap<string, OpenWork>): ReadonlyMap<string, number> =>
  new Map([...work].map(([policyId, counts]) => [policyId, counts.findings]));

export interface LibrarySuggestionSummary {
  /** Open housekeeping suggestions: what the Suggestions page lists as waiting. */
  readonly suggestions: number;
  /** Open missing-document gaps (they are findings, listed on the Findings page). */
  readonly missingDocuments: number;
  /** Open suggestions for the company profile. */
  readonly profile: number;
  /** Documents with at least one open housekeeping suggestion. */
  readonly documents: number;
}

/**
 * The shelf's one-line banner. "N suggestions are waiting" links to the
 * Suggestions page, so it counts what that page lists — housekeeping only.
 * Counting the gap suggestions too made the banner say 53 while the page and
 * the sidebar said 28.
 */
export function librarySuggestionSummary(
  suggestions: ReadonlyArray<{ readonly policyId: string | null; readonly status: string; readonly kind: string }>,
): LibrarySuggestionSummary {
  const waiting = suggestions.filter((item) => isOpenSuggestionStatus(item.status));
  const housekeeping = waiting.filter((item) => !isGapSuggestionKind(item.kind));
  return {
    suggestions: housekeeping.length,
    // Counted by kind, not by the absence of a document: two kinds carry no
    // policyId and mean opposite things (a gap in the library, and a change to
    // the company profile, which is not a document at all).
    missingDocuments: waiting.filter((item) => item.kind === "missing_document").length,
    profile: housekeeping.filter((item) => item.kind === "org_context").length,
    documents: new Set(housekeeping.flatMap((item) => (item.policyId === null ? [] : [item.policyId]))).size,
  };
}
